import {
  Briefcase,
  ChevronDown,
  LogOut,
  Lock,
  Palette,
  Settings,
  type LucideIcon,
} from "lucide-react";
import { type ReactElement, type ReactNode } from "react";

import { Avatar } from "../components/Avatar";
import { DropdownMenu } from "../components/DropdownMenu";
import { VidarioLogo } from "../components/VidarioLogo";
import { cn } from "../utils/cn";

/** Identity + role for the signed-in user rendered in the TopNav.
 * Kept in sync with @vidario/domain's Role union in Phase 2. */
export type NavRole = "recruiter" | "admin";

export interface NavUser {
  name: string;
  email: string;
  initials: string;
}

export interface NavItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  /** Whether this entry is currently active. Caller provides — no URL matching here. */
  active?: boolean;
  /** Also reachable from the user menu, so it leaves the bar on narrow screens (#124). */
  inUserMenu?: boolean;
  onSelect: () => void;
}

export interface TopNavProps {
  logoHref?: string;
  onLogoClick?: () => void;
  items: readonly NavItem[];
  userRole?: NavRole;
  user: NavUser;
  onSettings?: () => void;
  onLogout?: () => void;
  onDesignSystem?: () => void;
  /** Running release (or environment + commit), shown at the foot of the user menu. */
  versionLabel?: string;
  /** App-owned controls shown before the admin marker, e.g. "What's new". */
  actions?: ReactNode;
  /** A full-width strip above the header that scrolls with it, e.g. the
   * Anonym-Ansicht bar: it must stay visible while the page scrolls. */
  banner?: ReactNode;
  className?: string;
}

export function TopNav({
  onLogoClick,
  items,
  userRole = "recruiter",
  user,
  onSettings,
  onLogout,
  onDesignSystem,
  versionLabel,
  actions,
  banner,
  className,
}: TopNavProps): ReactElement {
  return (
    <div className="sticky top-0 z-40">
      {banner}
      <header className={cn("h-16 border-b border-border-default bg-neutral-0", className)}>
        <div className="mx-auto flex h-full max-w-[var(--container-max)] items-center justify-between gap-2 px-4 sm:px-8">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4 lg:gap-10">
            <button
              type="button"
              onClick={onLogoClick}
              aria-label="Zum Dashboard"
              className="inline-flex shrink-0 items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              {/* Phones get the mark alone: the header has no room for the wordmark. */}
              <span className="sm:hidden">
                <VidarioLogo withText={false} />
              </span>
              <span className="hidden sm:inline-flex">
                <VidarioLogo />
              </span>
            </button>
            <nav aria-label="Hauptnavigation" className="flex gap-1">
              {items.map((it) => {
                const Icon = it.icon;
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={it.onSelect}
                    aria-current={it.active ? "page" : undefined}
                    // Below lg the bar shows icons only (#139, #124); the name stays for screen readers.
                    aria-label={Icon ? it.label : undefined}
                    className={cn(
                      "h-9 items-center gap-2 rounded-md px-1.5 text-sm transition-colors sm:px-2 lg:px-3.5",
                      it.inUserMenu ? "hidden lg:inline-flex" : "inline-flex",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
                      it.active
                        ? "bg-primary-50 font-semibold text-primary-700"
                        : "font-medium text-neutral-700 hover:bg-neutral-50",
                    )}
                  >
                    {Icon && <Icon size={16} aria-hidden="true" />}
                    <span className={Icon ? "hidden lg:inline" : undefined}>{it.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* An admin's bar carries the Anonym switch too: tighter on a 320 px phone (#124).
              One more item there and it needs a menu drawer instead. */}
          <div
            className={cn("flex items-center sm:gap-3", userRole === "admin" ? "gap-1" : "gap-2")}
          >
            {actions}
            {userRole === "admin" && (
              <span
                aria-label="Administrator-Konto"
                className="hidden h-7 items-center gap-1.5 rounded-md bg-[var(--admin-marker-bg)] px-2.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--admin-marker)] lg:inline-flex"
              >
                <Lock size={11} strokeWidth={2.5} /> Admin
              </span>
            )}

            <DropdownMenu.Root>
              <DropdownMenu.Trigger
                className="inline-flex h-10 items-center gap-2.5 rounded-full border border-border-default bg-neutral-0 py-0 pr-1.5 pl-1.5 sm:pr-2.5 transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                aria-label="Benutzermenü"
              >
                <Avatar initials={user.initials} size={28} />
                <span className="hidden text-sm font-medium xl:inline">
                  {user.name.split(" ")[0]}
                </span>
                <ChevronDown size={14} className="hidden sm:block" />
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content>
                  <div className="mb-1 border-b border-border-default px-3 py-2.5">
                    <div className="text-sm font-semibold">{user.name}</div>
                    <div className="break-all text-xs text-text-tertiary">{user.email}</div>
                  </div>
                  {onSettings && (
                    <DropdownMenu.Item onSelect={onSettings}>
                      <Settings size={14} /> Einstellungen
                    </DropdownMenu.Item>
                  )}
                  {onDesignSystem && (
                    <DropdownMenu.Item onSelect={onDesignSystem}>
                      <Palette size={14} /> Design System
                    </DropdownMenu.Item>
                  )}
                  {onLogout && (
                    <DropdownMenu.Item onSelect={onLogout}>
                      <LogOut size={14} /> Abmelden
                    </DropdownMenu.Item>
                  )}
                  {versionLabel && (
                    <div className="mt-1 border-t border-border-default px-3 pb-1 pt-2 text-xs text-text-tertiary">
                      Version {versionLabel}
                    </div>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </div>
      </header>
    </div>
  );
}

/** Default icon set for the common nav items. Consumers can skip the helper
 * and pass NavItem[] directly. */
export const DEFAULT_NAV_ICONS = { Briefcase, Settings, Palette } as const;
